/**
 * Per-channel CHROME icon sets for the IM tabs.
 *
 * Split out of `messenger-theme.ts` on purpose: that module is pure data +
 * pure functions with node-env unit tests, and must not pull `lucide-react`
 * into them. Here we hold the icon COMPONENTS the header and composer render.
 *
 * All of it is decorative while this surface stays read-only (the reply
 * composer is tabs-plan P3, unapproved here), so every icon is rendered
 * `aria-hidden` and non-interactive.
 * It exists because the operator's direction is that the tab must read as the
 * real client ("literally replicate every little detail"), and the icon row is
 * a large part of that recognition.
 *
 * Telegram Web: search + overflow in the header; paperclip · input · emoji ·
 * mic in the composer. WhatsApp Web (2025 build): video-call + voice-call
 * alongside search + overflow in the header, and BOTH attach (`+`) and emoji sit
 * OUTSIDE the input pill to its left, with only the mic outside-right (measured
 * 2026-07-25). The header keeps video + voice as the 1:1 default: the thread
 * available to measure was a business/notification contact, whose header omits
 * the voice affordance — not representative of a person-to-person chat.
 */
import { MoreVertical, Paperclip, Phone, Plus, Search, Smile, Video, Mic } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface MessengerChrome {
  /** Header action icons, left-to-right. */
  headerIcons: LucideIcon[];
  /** Composer icons to the LEFT of the input. */
  composerLeadIcons: LucideIcon[];
  /** Composer icons to the RIGHT of the input. */
  composerTrailIcons: LucideIcon[];
}

const TELEGRAM: MessengerChrome = {
  headerIcons: [Search, MoreVertical],
  composerLeadIcons: [Paperclip],
  composerTrailIcons: [Smile, Mic],
};

const WHATSAPP: MessengerChrome = {
  headerIcons: [Video, Phone, Search, MoreVertical],
  // Measured 2026-07-25 off the live composer: attach (+) and emoji both sit
  // OUTSIDE the input pill to its left (attach leftmost), and ONLY the mic is
  // outside-right — the emoji is NOT a trailing icon as first shipped.
  composerLeadIcons: [Plus, Smile],
  composerTrailIcons: [Mic],
};

export function getMessengerChrome(channel: string): MessengerChrome {
  if (channel === "whatsapp") return WHATSAPP;
  return TELEGRAM;
}
